import { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '../../../urls';
import RecommendationModal from './recommendation-modal';
import { AppDataGrid } from '@/components/ui';
import { Loader } from '@/components/loader/loader';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { Pill } from '@/components/ds';
import { statusTone } from '@/theme/tokens';

const columns = [
  {
    field: 'name',
    flex: 1,
    headerName: 'Recommendation Name',
    renderCell: (params: any) => (
      <span style={{ color: '#16233A', fontWeight: 700 }}>{params.value}</span>
    ),
  },
  {
    field: 'type',
    headerName: 'Type',
    width: 150,
    renderCell: (params: any) => {
      const type = params.value ?? '';
      if (type.startsWith('AI-')) {
        return (
          <span
            title={`Found by the AI recommendations engine (${params.row.recommendation_source})`}>
            <Pill tone="ai">{`AI · ${type.slice(3)}`}</Pill>
          </span>
        );
      }
      return type ? <Pill tone="primary">{type}</Pill> : type;
    },
  },
  {
    field: 'resource_name',
    flex: 0.5,
    width: 80,
    headerName: 'Resource',
  },
  {
    field: 'monthly_savings',
    headerName: 'Monthly Savings',
    type: 'number',
    width: 120,
    valueFormatter: (params: any) => {
      if (params.value === null || params.value === undefined || params.value === 'n/a') {
        return 'n/a';
      }
      return formatCurrencyNoUnit(params.value);
    },
    renderCell: (params: any) => (
      <span style={{ color: '#16A55C', fontWeight: 900 }}>{params.formattedValue}</span>
    ),
  },
  {
    field: 'current_month_forecast',
    headerName: 'Monthly forecast',
    type: 'number',
    width: 120,
    valueFormatter: (params: any) => {
      if (params.value === null || params.value === undefined || params.value === 'n/a') {
        return 'n/a';
      }
      return formatCurrencyNoUnit(params.value);
    },
  },
  {
    field: 'username',
    width: 90,
    headerName: 'Responsible',
  },
    {
    field: 'status',
    width: 90,
    headerName: 'Status',
    renderCell: (params: any) => {
      const value = params.value ?? '';
      if (!value) return value;
      return <Pill tone={statusTone(value)}>{value}</Pill>;
    },
  },
];

interface RecommendationsTableProps {
  filterQueryParams: string;
  refreshKey?: number;
  onRecommendationAction?: () => void;
}

export function RecommendationsTable({
  filterQueryParams,
  refreshKey = 0,
  onRecommendationAction,
}: RecommendationsTableProps) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [error, setError] = useState<string | null>(null);

  const fetchRecommendations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWrapper.get(
        `${urls.DevOps.recommendations}?${filterQueryParams}`,
      );
      setItems(data);
    } catch (fetchError) {
      console.error('There was an error!', fetchError);
      setError('Failed to load recommendations.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterQueryParams]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations, refreshKey]);

  const handleRowClick = (params: any) => {
    setSelectedRecommendation(params.row);
  };

  const handleCloseModal = () => {
    setSelectedRecommendation(null);
  };

  const handleRecommendationAction = () => {
    setSelectedRecommendation(null);
    onRecommendationAction?.();
  };

  // map every empty cost item with 'n/a':
  const rows = items.map((item: any) => {
    return {
      ...item,
      current_cost: item.current_cost ?? 'n/a',
      current_month_forecast: item.current_month_forecast ?? 'n/a',
    };
  });

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No data for current filters
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <AppDataGrid
        rows={rows}
        // @ts-ignore
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 6, page: 0 },
          },
        }}
        onRowClick={handleRowClick}
        checkboxSelection={false}
      />
      <RecommendationModal
        recommendation={selectedRecommendation}
        onClose={handleCloseModal}
        onActionComplete={handleRecommendationAction}
      />
    </>
  );
}
